import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  DialogActions,
  DialogContent,
  useTheme,
} from '@mui/material';
import { customerService } from '../../services/customerService';
import { getCurrentUser } from '../../services/authService';

/**
 * Shared form component for adding/editing customers
 * @param {Object} props Component props
 * @param {Function} props.onSuccess Callback when customer is created/updated successfully
 * @param {Function} props.onCancel Callback when form is cancelled
 * @param {Object} props.initialData Initial form data (for editing)
 * @returns {JSX.Element} Customer form component
 */
const CustomerForm = ({ onSuccess, onCancel, initialData = null }) => {
  const theme = useTheme();
  const user = getCurrentUser();

  // Form state
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    contactPerson: initialData?.contactPerson || '',
    address: initialData?.address || '',
    website: initialData?.website || '',
    notes: initialData?.notes || '',
    gst: initialData?.gst || '',
    openingBalance: initialData?.openingBalance || 0,
    companyId: initialData?.companyId || user?.companyId || 1
  });

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Parse the opening balance as a float with 2 decimal places
      const initialBalance = parseFloat(parseFloat(formData.openingBalance || 0).toFixed(2));
      const customerData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        contactPerson: formData.contactPerson,
        address: formData.address,
        website: formData.website,
        notes: formData.notes,
        gst: formData.gst,
        openingBalance: initialBalance,  // Set opening balance
        creditBalance: initialBalance,  // Set credit balance same as opening balance
        companyId: formData.companyId
      };
      
      let response;
      if (initialData && initialData.id) {
        response = await customerService.updateCustomer(initialData.id, customerData);
      } else {
        response = await customerService.createCustomer(customerData);
      }
      onSuccess(response);
    } catch (error) {
      console.error('Error saving customer:', error);
      // Let parent component handle error display
      onSuccess(null, error.response?.data?.message || 'Failed to save customer');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  return (
    <>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            margin="normal"
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            fullWidth
            label="Contact Person"
            name="contactPerson"
            value={formData.contactPerson}
            onChange={handleInputChange}
            required
            margin="normal"
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            margin="normal"
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            required
            margin="normal"
            inputProps={{ maxLength: 20 }}
          />
          <TextField
            fullWidth
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            required
            margin="normal"
            multiline
            rows={2}
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            fullWidth
            label="Website"
            name="website"
            value={formData.website}
            onChange={handleInputChange}
            margin="normal"
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            fullWidth
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={2}
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            fullWidth
            label="GST Number"
            name="gst"
            value={formData.gst}
            onChange={handleInputChange}
            margin="normal"
            inputProps={{ maxLength: 50 }}
          />
          <TextField
            fullWidth
            label="Opening Balance"
            name="openingBalance"
            type="number"
            value={formData.openingBalance}
            onChange={handleInputChange}
            margin="normal"
            inputProps={{ step: "0.01", min: "0" }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {initialData ? 'Save Changes' : 'Add Customer'}
        </Button>
      </DialogActions>
    </>
  );
};

export default CustomerForm; 